// components/overlay/PromoPanel.jsx
// Rotating marketing spotlight ("The Griddle Gazette") — curated showcase
// items + press-logo card, driven by the queue from useBoothShowcase.
//
// Shared by /overlay/booth and /overlay/break.
//
// Performance: only the active queue entry is rendered (stable compositor
// cost, no stacked layers). Slides transition with a fade-through — fade out,
// swap content while invisible, fade in — the same single-layer pattern as
// the Break screen's Did You Know card. Opacity-only transition: composites
// for free, never two slides painted at once. All card images are warmed into
// the browser cache up front, and every card image renders through FadeImg —
// the cache warm doesn't keep decoded bitmaps around, so an image whose
// decode outruns the container fade soft-fades in on its own instead of
// hard-popping (the old behavior, inconsistent per cache state).

import { useEffect, useRef, useState } from "react";
import FadeImg from "./FadeImg";
import { COMBOS, fmtCents, comboUpgradeDeltaCents } from "../../utils/combos";

const GOLD = "var(--jop-gold)";
const CREAM = "var(--jop-cream)";
const GOLD_RGB = "var(--jop-gold-rgb)";
const D = "var(--jop-display)";

// ── PRESS LOGOS (same source as Main.jsx CredentialStrip) ────────────────────
// 6 logos fill a 3-row × 2-col grid cleanly in the tall promo panel.
const PRESS = [
  { name: "Today",                file: "/press/Today_Show_Logo.png" },
  { name: "Good Morning America", file: "/press/Good_Morning_America_Logo.png" },
  { name: "Hell's Kitchen",       file: "/press/Hells_Kitchen_Logo.png" },
  { name: "Disney Parks",         file: "/press/Disney_Parks_Logo.png" },
  { name: "Nickelodeon",          file: "/press/Nickelodeon_logo.svg" },
  { name: "Buzzfeed",             file: "/press/Buzzfeed_Logo.png" },
];

const cardShell = {
  width: "100%", height: "100%",
  boxSizing: "border-box",
  display: "flex", flexDirection: "column",
  alignItems: "center", justifyContent: "center",
  gap: "0.6rem",
  padding: "0.9rem",
};
const itemHeadlineStyle = {
  width: "100%",
  flexShrink: 0,
  color: GOLD, fontFamily: D, fontWeight: 900,
  fontSize: "2rem", lineHeight: 1.1,
  textAlign: "center",
  letterSpacing: "0.02em",
  textShadow: "0 2px 10px rgba(0,0,0,0.6), 0 0 22px rgba(var(--jop-gold-rgb), 0.25)",
  padding: "0 0.4rem",
  wordWrap: "break-word",
};
const itemBodyStyle = {
  width: "100%",
  flexShrink: 0,
  boxSizing: "border-box",
  color: CREAM, fontFamily: D, fontWeight: 600,
  fontSize: "1.2rem", lineHeight: 1.4,
  textAlign: "center",
  letterSpacing: "0.01em",
  textShadow: "0 2px 6px rgba(0,0,0,0.5)",
  padding: "0 2.8rem",   // narrower body column for breathing room
  whiteSpace: "pre-wrap",
  wordWrap: "break-word",
  opacity: 0.95,
};

// Item card: optional headline (big, bold) + body (sentence-case prose with
// generous side margins) stacked at the top, photo anchored at the bottom
// with object-fit: contain so varying image heights all read cleanly.
//
// wide (short full-width band, e.g. Booth portrait bottom strip): text block
// on the left, image on the right — stacking in a 1080×~200 band leaves both
// unreadable, side-by-side uses the shape.
function PromoItemCard({ item, wide }) {
  const hasHeadline = !!item.headline;
  const hasBody = !!item.caption;
  if (wide) {
    return (
      <div style={{
        width: "100%", height: "100%", boxSizing: "border-box",
        display: "flex", flexDirection: "row", alignItems: "center",
        gap: "1.4rem",
        padding: "0.7rem 1.3rem",
      }}>
        <div style={{
          flex: "1.35 1 0", minWidth: 0,
          display: "flex", flexDirection: "column",
          justifyContent: "center", gap: "0.5rem",
        }}>
          {hasHeadline && (
            <div style={{ ...itemHeadlineStyle, textAlign: "left", padding: 0, fontSize: "2.3rem" }}>
              {item.headline}
            </div>
          )}
          {hasBody && (
            <div style={{
              ...itemBodyStyle,
              textAlign: "left", padding: 0, fontSize: "1.6rem", lineHeight: 1.3,
              overflow: "hidden",
              display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
            }}>
              {item.caption}
            </div>
          )}
        </div>
        <div style={{
          flex: "1 1 0", minWidth: 0, height: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
        }}>
          <FadeImg
            src={item.imageUrl}
            alt={item.headline || item.caption || item.category || "Showcase"}
            style={{
              maxWidth: "100%", maxHeight: "100%",
              width: "auto", height: "auto",
              objectFit: "contain",
            }}
          />
        </div>
      </div>
    );
  }
  return (
    <div style={{
      ...cardShell,
      padding: "1.1rem 0.9rem",
      gap: "0.4rem",
      justifyContent: "space-between",
    }}>
      {(hasHeadline || hasBody) ? (
        <div style={{
          flexShrink: 0,
          width: "100%",
          display: "flex", flexDirection: "column",
          alignItems: "center",
          gap: "0.55rem",
        }}>
          {hasHeadline && (
            <div style={itemHeadlineStyle}>{item.headline}</div>
          )}
          {hasBody && (
            <div style={itemBodyStyle}>{item.caption}</div>
          )}
        </div>
      ) : null}

      <div style={{
        flex: 1, minHeight: 0, width: "100%",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        paddingTop: "0.6rem",
        overflow: "hidden",
      }}>
        <FadeImg
          src={item.imageUrl}
          alt={item.headline || item.caption || item.category || "Showcase"}
          style={{
            maxWidth: "100%", maxHeight: "100%",
            width: "auto", height: "auto",
            objectFit: "contain",
          }}
        />
      </div>
    </div>
  );
}

function PromoPressCard({ wide }) {
  return (
    <div style={wide ? {
      width: "100%", height: "100%", boxSizing: "border-box",
      display: "flex", flexDirection: "row", alignItems: "center",
      gap: "1rem", padding: "0.5rem 1.2rem",
    } : cardShell}>
      <div style={{
        color: CREAM, fontFamily: D, fontWeight: 900,
        fontSize: wide ? "1.5rem" : "1.6rem", letterSpacing: "0.22em",
        textAlign: "center", textShadow: "0 2px 6px rgba(0,0,0,0.5)",
        opacity: 0.95,
        ...(wide
          ? { flexShrink: 0, width: "150px", lineHeight: 1.3 }
          : { marginBottom: "0.3rem", flexShrink: 0 }),
      }}>
        AS&nbsp;SEEN ON
      </div>
      <div style={{
        flex: 1, minHeight: 0, width: "100%",
        boxSizing: "border-box",
        // wide: the card is a ROW flex with alignItems center — without
        // stretch the grid is content-sized and the logos overflow the band.
        alignSelf: wide ? "stretch" : undefined,
        display: "grid",
        gridTemplateColumns: wide ? "repeat(3, 1fr)" : "1fr 1fr",
        gridTemplateRows: wide ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
        gap: "0.6rem 1rem",
        padding: "0.3rem",
      }}>
        {PRESS.map((p) => (
          <div key={p.name} style={{
            minWidth: 0, minHeight: 0,
            padding: "0.4rem 0.6rem",
            boxSizing: "border-box",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FadeImg
              src={p.file}
              alt={p.name}
              style={{
                maxWidth: "100%", maxHeight: "78%",
                width: "auto", height: "auto",
                objectFit: "contain",
                // Full-color originals — do NOT white-out with
                // brightness(0) invert(1): logos that mix white keylines
                // with color (GMA) merge into an illegible blob.
                filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.45))",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// Combo upsell card: text-only (no image to warm, no compositor cost), driven
// by shift.activeComboKeys via useBoothShowcase. Deliberately NO QR — the
// SummonBanner owns the one QR story per scene; this card sells, the banner
// routes. Price shows the à-la-carte strikethrough so the deal reads at a
// glance from across the booth.
function PromoComboCard({ comboKey, goodsOnly, priceCentsOverride, compareCentsOverride, wide }) {
  const combo = COMBOS[comboKey];
  if (!combo) return null;
  // goodsOnly (free-pancake shows selling take-home goods): goods bundle
  // price + party-appropriate label/pitch — the pancake itself stays free
  // and this card never implies otherwise.
  const label = goodsOnly ? combo.goodsLabel : combo.label;
  const pitch = goodsOnly ? combo.goodsPitch : combo.pitch;
  const imageUrl = goodsOnly ? combo.goodsImageUrl : null; // real kit photo (Square CDN)
  // Overrides come from the queue entry (Road Mode shift pricing, derived in
  // useBoothShowcase) — the card must always show what checkout will charge.
  const priceCents = priceCentsOverride ?? (goodsOnly ? comboUpgradeDeltaCents(comboKey) : combo.priceCents);
  const compareCents = compareCentsOverride ?? (goodsOnly ? combo.goodsAlaCarteCents : combo.alaCarteCents);
  const price = (
    <span style={{ whiteSpace: "nowrap" }}>
      {fmtCents(priceCents)}
      {compareCents > priceCents && (
        <span style={{ opacity: 0.55, textDecoration: "line-through", fontSize: "0.7em", marginLeft: "0.35em" }}>
          {fmtCents(compareCents)}
        </span>
      )}
    </span>
  );
  if (wide) {
    return (
      <div style={{
        width: "100%", height: "100%", boxSizing: "border-box",
        display: "flex", flexDirection: "row", alignItems: "center",
        gap: "1.4rem", padding: "0.7rem 1.3rem",
      }}>
        <div style={{ flex: "1.35 1 0", minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: "0.5rem" }}>
          <div style={{ ...itemHeadlineStyle, textAlign: "left", padding: 0, fontSize: "2.3rem" }}>
            {combo.emoji} {label}
          </div>
          <div style={{
            ...itemBodyStyle, textAlign: "left", padding: 0, fontSize: "1.5rem", lineHeight: 1.3,
            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
          }}>
            {pitch}
          </div>
        </div>
        {imageUrl && (
          <div style={{ flex: "0 0 auto", height: "100%", maxWidth: "22%", display: "flex", alignItems: "center", overflow: "hidden" }}>
            <FadeImg src={imageUrl} alt={label} style={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }} />
          </div>
        )}
        <div style={{
          flex: "0 0 auto", color: GOLD, fontFamily: D, fontWeight: 900,
          fontSize: "3rem", textShadow: `0 2px 10px rgba(0,0,0,0.6), 0 0 22px rgba(${GOLD_RGB}, 0.25)`,
        }}>
          {price}
        </div>
      </div>
    );
  }
  return (
    <div style={{ ...cardShell, justifyContent: "center", gap: "0.8rem" }}>
      <div style={itemHeadlineStyle}>{combo.emoji} {label}</div>
      <div style={{
        color: GOLD, fontFamily: D, fontWeight: 900, fontSize: "3.2rem",
        textShadow: `0 2px 10px rgba(0,0,0,0.6), 0 0 22px rgba(${GOLD_RGB}, 0.25)`,
      }}>
        {price}
      </div>
      {imageUrl && (
        <div style={{ flex: "0 1 auto", minHeight: 0, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          <FadeImg src={imageUrl} alt={label} style={{ maxWidth: "70%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain", filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.45))" }} />
        </div>
      )}
      <div style={{ ...itemBodyStyle, fontSize: "1.3rem" }}>{pitch}</div>
      <div style={{
        color: CREAM, fontFamily: D, fontWeight: 700, fontSize: "1rem",
        letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.8,
        textShadow: "0 2px 6px rgba(0,0,0,0.5)",
      }}>
        Ask at the booth
      </div>
    </div>
  );
}

// Road Mode menu card: the booth's living price menu, derived ENTIRELY from
// the shift's pricing block (rows built in useBoothShowcase) — it can never
// disagree with what /join actually charges. Text-only, no QR (SummonBanner
// owns the QR story), leads the Gazette rotation.
function PromoMenuCard({ menu, wide }) {
  if (!menu || !Array.isArray(menu.rows) || menu.rows.length === 0) return null;
  const row = (r, i, size) => (
    <div key={i} style={{
      display: "flex", justifyContent: "space-between", alignItems: "baseline",
      gap: "0.8rem", minWidth: 0,
    }}>
      <span style={{
        color: CREAM, fontFamily: D, fontWeight: 700, fontSize: size,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        textShadow: "0 2px 6px rgba(0,0,0,0.5)",
      }}>
        {r.icon} {r.name}
      </span>
      <span style={{
        color: GOLD, fontFamily: D, fontWeight: 900, fontSize: size,
        whiteSpace: "nowrap", textShadow: `0 2px 8px rgba(0,0,0,0.55), 0 0 18px rgba(${GOLD_RGB}, 0.25)`,
      }}>
        {r.price}
      </span>
    </div>
  );
  if (wide) {
    // Short portrait band: one-line header strip (title left, tax right),
    // then two columns of big menu rows filling the band, note as a slim
    // footer. Row size scales with column depth so 4 items read from across
    // the booth and 8 still fit.
    const half = Math.ceil(menu.rows.length / 2);
    const rowSize = half <= 2 ? "1.95rem" : half === 3 ? "1.55rem" : "1.3rem";
    return (
      <div style={{
        width: "100%", height: "100%", boxSizing: "border-box",
        display: "flex", flexDirection: "column",
        gap: "0.35rem", padding: "0.6rem 1.5rem 0.55rem",
      }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "1rem", flexShrink: 0 }}>
          <span style={{
            color: GOLD, fontFamily: D, fontWeight: 900, fontSize: "1.5rem",
            textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap",
            textShadow: `0 2px 10px rgba(0,0,0,0.6), 0 0 22px rgba(${GOLD_RGB}, 0.25)`,
          }}>
            🥞 Today's Menu
          </span>
          {menu.taxLine && (
            <span style={{ color: CREAM, fontFamily: D, fontSize: "1rem", opacity: 0.6, whiteSpace: "nowrap" }}>{menu.taxLine}</span>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", gap: "2.6rem", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: "0.4rem" }}>
            {menu.rows.slice(0, half).map((r, i) => row(r, i, rowSize))}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: "0.4rem" }}>
            {menu.rows.slice(half).map((r, i) => row(r, i + half, rowSize))}
          </div>
        </div>
        {menu.note && (
          <div style={{
            color: CREAM, fontFamily: D, fontSize: "1rem", opacity: 0.7, flexShrink: 0,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {menu.note}
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{ ...cardShell, justifyContent: "center", gap: "0.6rem" }}>
      <div style={itemHeadlineStyle}>🥞 Today's Menu</div>
      <div style={{ width: "min(92%, 30rem)", display: "flex", flexDirection: "column", gap: "0.55rem" }}>
        {menu.rows.map((r, i) => row(r, i, "1.7rem"))}
      </div>
      {menu.note && (
        <div style={{ ...itemBodyStyle, fontSize: "1.05rem", opacity: 0.85 }}>{menu.note}</div>
      )}
      {menu.taxLine && (
        <div style={{ color: CREAM, fontFamily: D, fontSize: "0.9rem", opacity: 0.6 }}>{menu.taxLine}</div>
      )}
    </div>
  );
}

// Pancake card: cutout image at top, title + description filling the bottom.
// Currently UNUSED — pancake spotlight is hard-disabled in useBoothShowcase
// while we diagnose OBS compositor lag. Kept here for easy re-enable.
// eslint-disable-next-line no-unused-vars
function PromoPancakeCard({ pancake }) {
  const title = pancake?.title || "Untitled";
  const src = pancake?.cutoutUrl;
  const description = pancake?.aiDescription
    || pancake?.testimonial?.text
    || null;
  const eyebrow = pancake?.source === "gallery"
    ? "🥞 From the gallery"
    : "🥞 Fresh off the griddle";
  return (
    <div style={{
      ...cardShell,
      justifyContent: "flex-start",
      gap: "0.6rem",
    }}>
      <div style={{
        color: GOLD, fontFamily: D, fontWeight: 700,
        fontSize: "1rem", letterSpacing: "0.08em",
        textTransform: "uppercase", textAlign: "center",
        opacity: 0.85,
        flexShrink: 0,
      }}>
        {eyebrow}
      </div>

      <div style={{
        width: "100%",
        flex: "0 0 52%",
        minHeight: 0,
        boxSizing: "border-box",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        {src ? (
          <FadeImg
            src={src}
            alt={title}
            style={{
              maxWidth: "100%", maxHeight: "100%",
              width: "auto", height: "auto",
              objectFit: "contain",
            }}
          />
        ) : null}
      </div>

      <div style={{
        color: CREAM, fontFamily: D, fontWeight: 900,
        fontSize: "1.45rem", lineHeight: 1.15, textAlign: "center",
        textShadow: "0 2px 6px rgba(0,0,0,0.5)",
        flexShrink: 0,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        maxWidth: "100%",
        padding: "0 0.2rem",
      }}>
        "{title}"
      </div>

      {description ? (
        <div style={{
          flex: 1, minHeight: 0,
          color: CREAM, opacity: 0.9, fontFamily: D, fontWeight: 500,
          fontSize: "1.05rem", lineHeight: 1.35,
          textAlign: "center",
          padding: "0 0.3rem 0.2rem",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 5,
          WebkitBoxOrient: "vertical",
        }}>
          {description}
        </div>
      ) : (
        <div style={{ flex: 1 }} />
      )}
    </div>
  );
}

// ── ROTATING PROMO PANEL ─────────────────────────────────────────────────────
// Per-entry duration: each queue entry carries its own durationSec; we drive
// the rotation via setTimeout chained off the current entry's duration.
const FADE_MS = 500;

export default function PromoPanel({ queue, title = "📰 The Griddle Gazette", wide = false }) {
  const [idx, setIdx] = useState(0);
  const [vis, setVis] = useState(true);

  // Hold the latest queue in a ref so the rotation timer doesn't tear down
  // + restart on every render.
  const queueRef = useRef(queue);
  queueRef.current = queue;

  // Warm the cache for every image the rotation will show.
  const preloadedRef = useRef(new Set());
  useEffect(() => {
    const urls = [];
    queue.forEach((entry) => {
      if (entry.kind === "item" && entry.item?.imageUrl) urls.push(entry.item.imageUrl);
      if (entry.kind === "press") PRESS.forEach((p) => urls.push(p.file));
      if (entry.kind === "combo" && entry.goodsOnly && COMBOS[entry.comboKey]?.goodsImageUrl) urls.push(COMBOS[entry.comboKey].goodsImageUrl);
    });
    urls.forEach((u) => {
      if (preloadedRef.current.has(u)) return;
      preloadedRef.current.add(u);
      const img = new Image();
      img.src = u;
    });
  }, [queue]);

  useEffect(() => {
    const q = queueRef.current;
    if (q.length < 2) return;
    const entry = q[idx % q.length];
    const dur = Math.max(3, entry?.durationSec ?? 12) * 1000;
    let swapT;
    const t = setTimeout(() => {
      setVis(false);
      swapT = setTimeout(() => {
        const latest = queueRef.current;
        setIdx((idx + 1) % latest.length);
        setVis(true);
      }, FADE_MS);
    }, dur);
    return () => { clearTimeout(t); clearTimeout(swapT); };
  }, [idx, queue.length]);

  useEffect(() => {
    if (queue.length > 0 && idx >= queue.length) setIdx(0);
  }, [queue.length, idx]);

  return (
    <div style={{
      flex: 1, minHeight: 0,
      display: "flex", flexDirection: "column", gap: "0.6rem",
    }}>
      {/* wide (short band): no title header — the card content is
          self-explanatory and the band is short enough that the header
          visibly taxes the spotlight itself. */}
      {!wide && (
        <div style={{
          color: GOLD, fontFamily: D, fontWeight: 700,
          fontSize: "1.2rem", textTransform: "uppercase",
          letterSpacing: "0.08em",
          textAlign: "center",
          padding: "0.2rem 0 0.4rem",
          borderBottom: `2px solid rgba(${GOLD_RGB}, 0.33)`,
          flexShrink: 0,
        }}>
          {title}
        </div>
      )}
      <div style={{
        flex: 1, minHeight: 0,
        boxSizing: "border-box",
        background: "rgba(0,0,0,0.22)",
        border: `2px solid rgba(${GOLD_RGB}, 0.33)`,
        borderRadius: "14px",
        position: "relative",
        overflow: "hidden",
      }}>
        {queue.length === 0 ? (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: CREAM, opacity: 0.5, fontFamily: D, fontSize: "1rem",
            textAlign: "center", padding: "1rem",
          }}>
            Add showcase images in Stream Setup →
          </div>
        ) : (() => {
          // Only render the active layer. Browser caches images naturally
          // after first display, so subsequent rotations re-use the cache.
          // The wrapper is deliberately UNKEYED so it persists across slide
          // swaps — the opacity transition runs on the same element (content
          // is swapped while opacity is 0, then faded back in).
          const entry = queue[idx % queue.length];
          if (!entry) return null;
          return (
            <div style={{
              position: "absolute", inset: 0,
              opacity: vis ? 1 : 0,
              transition: `opacity ${FADE_MS}ms ease`,
            }}>
              {entry.kind === "item"  ? <PromoItemCard item={entry.item} wide={wide} /> :
               entry.kind === "press" ? <PromoPressCard wide={wide} /> :
               entry.kind === "menu"  ? <PromoMenuCard menu={entry.menu} wide={wide} /> :
               entry.kind === "combo" ? <PromoComboCard comboKey={entry.comboKey} goodsOnly={entry.goodsOnly} priceCentsOverride={entry.priceCents} compareCentsOverride={entry.compareCents} wide={wide} /> :
               null}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
